export * from './connections';
export * from './core-catalogs';
export * from './crowdex-app';
export * from './crowdex-manifest';

import {
  connectionMethodSchema,
  connectionMethodSummarySchema,
  connectionTypeDescriptorSchema,
} from './connections';
import { crowdexPluginDetailsSchema } from './crowdex-plugin-details';
import { pluginDisplayName } from './plugin-name';
import { pluginSettingsSchema, pluginSettingValueSchema } from './settings';

export * from './connections-capability';
export * from './crowdex-plugin-details';
export * from './inference-capability';
export * from './mcp-connections';
export * from './pages';
export * from './plugin-kind';
export * from './plugin-name';
export * from './settings';

import { z } from 'zod';
import { pluginRequiresSchema, serviceIdSchema } from './plugin-requirements';

export * from './plugin-requirements';

export * from './tool-ref';
export * from './workbench';

export const pluginNameSchema = z
  .string()
  .min(1)
  .max(96)
  .regex(/^[a-z0-9][a-z0-9._-]*$/);

const pluginInterfaceSchema = z
  .object({
    displayName: z.string().min(1).optional(),
    shortDescription: z.string().optional(),
    longDescription: z.string().optional(),
    logo: z.string().optional(),
    screenshots: z.array(z.string()).optional(),
  })
  .passthrough();

/**
 * A connector the plugin brings. `inherit` takes the catalog service's auth, actions and triggers as
 * they are; a plugin may only narrow them to a list of action or trigger ids.
 */
export const pluginConnectorContributionSchema = z
  .object({
    service: serviceIdSchema,
    actions: z
      .union([z.literal('inherit'), z.array(z.string().min(1).max(160)).max(512)])
      .default('inherit'),
    triggers: z
      .union([z.literal('inherit'), z.array(z.string().min(1).max(160)).max(512)])
      .default('inherit'),
    /** The connection type of a plugin's own service (`<plugin>/<service>`): what the user enters. */
    ...connectionTypeDescriptorSchema.partial().shape,
  })
  .strict()
  .refine(
    (connector) =>
      connector.service.includes('/') ||
      (connector.auth === undefined && connector.test === undefined),
    "A catalog service's auth comes from the catalog; only a plugin's own service declares it.",
  );
export type PluginConnectorContribution = z.infer<typeof pluginConnectorContributionSchema>;

/** An agent id of the public ACP registry. */
export const acpRegistryAgentIdSchema = z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,127}$/);

/** One service of a published service index. */
export const connectorServiceIndexEntrySchema = z.object({
  id: z
    .string()
    .regex(/^[a-z0-9_][a-z0-9_-]*$/)
    .max(128),
  name: z.string().min(1).max(256),
  description: z.string().max(4096).optional(),
  categories: z.array(z.string().max(128)).max(16),
  auth: z.array(z.enum(['none', 'credentials', 'oauth2'])),
  actions: z.number().int().nonnegative(),
  triggers: z.number().int().nonnegative(),
  /**
   * Each action and trigger as `[id, title, kind, methods?]`, `methods` being the indexes in
   * `methods` of those that can run it, when not all of the catalog's methods can.
   */
  operations: z
    .array(
      z
        .tuple([z.string().min(1).max(160), z.string().max(256), z.enum(['action', 'trigger'])])
        .rest(z.array(z.number().int().nonnegative()).max(64)),
    )
    .max(1024)
    .default([]),
  /** The ways to connect the service: the catalog's methods, then its MCP servers. */
  methods: z.array(connectionMethodSchema).max(64).default([]),
});
export type ConnectorServiceIndexEntry = z.infer<typeof connectorServiceIndexEntrySchema>;
/** One action or trigger of a service: `[id, title, kind, methods?]`. */
export type ConnectorOperation = ConnectorServiceIndexEntry['operations'][number];
/** What one action of a service does and takes (`connectors.action`). */
export type ConnectorActionDetail = {
  name: string;
  description: string;
  kind: 'action' | 'trigger';
  inputSchema: Record<string, unknown>;
};
/** How a service's connector is connected and what it runs (`connectors.service`). */
export type ConnectorServiceActions = Pick<ConnectorServiceIndexEntry, 'methods' | 'operations'>;

/**
 * The service index of the connector catalog crowlink serves (`/v1/catalog/services`),
 * beside its logos (`connectorServiceIconsSchema`) and how each service connects
 * (`connectorServiceConnectionsSchema`, the connection types the hub offers for these services).
 */
export const connectorServiceIndexSchema = z.object({
  schemaVersion: z.literal(1),
  services: z.array(connectorServiceIndexEntrySchema),
});

/** Small service logos as image data URIs, by service id. */
export const connectorServiceIconsSchema = z.object({
  schemaVersion: z.literal(1),
  icons: z.record(
    z.string(),
    z.string().regex(/^data:image\/(?:webp|png|jpeg);base64,[A-Za-z0-9+/=]+$/),
  ),
});
export type ConnectorServiceIndex = z.infer<typeof connectorServiceIndexSchema>;

/** The connection type of each service of an index, by service id: data the hub runs, never code. */
export const connectorServiceConnectionsSchema = z.object({
  schemaVersion: z.literal(1),
  types: z.record(
    z
      .string()
      .regex(/^[a-z0-9_][a-z0-9_-]*$/)
      .max(128),
    connectionTypeDescriptorSchema,
  ),
});
export type ConnectorServiceConnections = z.infer<typeof connectorServiceConnectionsSchema>;

/** Files of the package served as MCP resources, at `resource://<plugin>/<id>/<path>`. */
export const pluginResourceContributionSchema = z
  .object({
    id: z
      .string()
      .regex(/^[a-z0-9][a-z0-9_-]*$/)
      .max(64),
    path: z.string().min(1).max(512),
    title: z.string().min(1).max(160).optional(),
    description: z.string().max(1024).optional(),
  })
  .strict();
export type PluginResourceContribution = z.infer<typeof pluginResourceContributionSchema>;

export const pluginManifestSchema = z
  .object({
    name: pluginNameSchema.optional(),
    version: z.string().optional(),
    description: z.string().optional(),
    author: z
      .union([z.string(), z.object({ name: z.string(), url: z.string().optional() }).passthrough()])
      .optional(),
    homepage: z.string().optional(),
    repository: z.union([z.string(), z.object({ url: z.string() }).passthrough()]).optional(),
    license: z.string().optional(),
    interface: pluginInterfaceSchema.optional(),
    apps: z.union([z.string(), z.array(z.string())]).optional(),
    /** The service this plugin is about: it replaces the service's generated catalog entry. */
    service: serviceIdSchema.optional(),
    /** A provisioned plugin is installed for everyone; one off by default waits to be enabled. */
    enabledByDefault: z.boolean().optional(),
    settings: pluginSettingsSchema.optional(),
    /** The accounts the plugin uses. */
    requires: pluginRequiresSchema.optional(),
    contributions: z
      .object({
        skills: z.union([z.string(), z.array(z.string())]).optional(),
        mcpServers: z.union([z.string(), z.array(z.string())]).optional(),
        connectors: z.array(pluginConnectorContributionSchema).max(64).optional(),
        resources: z.array(pluginResourceContributionSchema).max(64).optional(),
        /** Agents of the public ACP registry the plugin offers, launched from their distribution. */
        registryAgents: z.array(acpRegistryAgentIdSchema).max(64).optional(),
      })
      .strict()
      .optional(),
  })
  .passthrough()
  .superRefine((manifest, context) => {
    for (const key of ['skills', 'mcpServers'] as const) {
      if (manifest[key] !== undefined) {
        context.addIssue({
          code: 'custom',
          path: [key],
          message: `Plugin ${key} must be declared under contributions.`,
        });
      }
    }
    const connectorServices = (manifest.contributions?.connectors ?? []).map(
      (connector) => connector.service,
    );
    for (const [index, service] of connectorServices.entries()) {
      if (connectorServices.indexOf(service) !== index) {
        context.addIssue({
          code: 'custom',
          path: ['contributions', 'connectors', index, 'service'],
          message: `Duplicate service: ${service}.`,
        });
      }
      const namespace = service.includes('/') ? service.slice(0, service.indexOf('/')) : null;
      if (namespace !== null && namespace !== manifest.name) {
        context.addIssue({
          code: 'custom',
          path: ['contributions', 'connectors', index, 'service'],
          message:
            'A plugin can only bring connectors of catalog services or of its own namespace.',
        });
      }
    }
    const resourceIds = (manifest.contributions?.resources ?? []).map((resource) => resource.id);
    for (const [index, id] of resourceIds.entries()) {
      if (resourceIds.indexOf(id) !== index) {
        context.addIssue({
          code: 'custom',
          path: ['contributions', 'resources', index, 'id'],
          message: `Duplicate resource id: ${id}.`,
        });
      }
    }
  });
export type PluginManifest = z.infer<typeof pluginManifestSchema>;

/** The files a Claude Code plugin has, which its manifest may leave undeclared. */
export type ClaudeCodePluginFiles = {
  skills: boolean;
  mcp: boolean;
  commands: boolean;
  agents: boolean;
  hooks: boolean;
};

/**
 * A Claude Code plugin manifest (`.claude-plugin/plugin.json`) in the Crowdex layout. Skills and
 * MCP servers are imported. Slash commands, subagents and hooks have no Crowdex equivalent and
 * are left out with a notice; MCP servers declared inline are written to `inlineMcpPath`.
 */
export function claudeCodePluginManifest(
  input: unknown,
  files: ClaudeCodePluginFiles,
  inlineMcpPath = './.crowdex-mcp.json',
): { manifest: unknown; inlineMcp: unknown; notices: string[] } {
  const source =
    typeof input === 'object' && input !== null && !Array.isArray(input)
      ? (input as Record<string, unknown>)
      : {};
  const text = (key: string) => (typeof source[key] === 'string' ? source[key] : undefined);
  const name = text('name');
  const inlineMcp =
    typeof source['mcpServers'] === 'object' && source['mcpServers'] !== null
      ? { mcpServers: source['mcpServers'] }
      : undefined;
  const mcpServers =
    inlineMcp !== undefined
      ? inlineMcpPath
      : (text('mcpServers') ?? (files.mcp ? './.mcp.json' : undefined));
  const skills = files.skills ? './skills' : undefined;
  const notices = [
    ...(files.commands || source['commands'] !== undefined
      ? ['Claude Code slash commands have no Crowdex equivalent and were not imported.']
      : []),
    ...(files.agents || source['agents'] !== undefined
      ? ['Claude Code subagents have no Crowdex equivalent and were not imported.']
      : []),
    ...(files.hooks || source['hooks'] !== undefined
      ? ['Claude Code hooks are not supported and were not imported.']
      : []),
  ];
  const author = source['author'];
  return {
    manifest: {
      ...(name === undefined ? {} : { name }),
      ...(text('version') === undefined ? {} : { version: text('version') }),
      ...(text('description') === undefined ? {} : { description: text('description') }),
      ...(typeof author === 'string' || (typeof author === 'object' && author !== null)
        ? { author }
        : {}),
      ...(text('homepage') === undefined ? {} : { homepage: text('homepage') }),
      ...(text('repository') === undefined ? {} : { repository: text('repository') }),
      ...(text('license') === undefined ? {} : { license: text('license') }),
      ...(name === undefined ? {} : { interface: { displayName: pluginDisplayName(name) } }),
      ...(skills === undefined && mcpServers === undefined
        ? {}
        : {
            contributions: {
              ...(skills === undefined ? {} : { skills }),
              ...(mcpServers === undefined ? {} : { mcpServers }),
            },
          }),
    },
    inlineMcp,
    notices,
  };
}

/**
 * A Codex plugin manifest in the Crowdex layout. Codex declares `skills` and `mcpServers` at the
 * top level; Crowdex keeps them under `contributions`, so marketplace packages are translated when
 * they are read from the Codex marketplace or installed from it.
 */
export function codexPluginManifest(input: unknown): unknown {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) return input;
  const { skills, mcpServers, ...manifest } = input as Record<string, unknown>;
  if (skills === undefined && mcpServers === undefined) return input;
  const contributions =
    typeof manifest['contributions'] === 'object' && manifest['contributions'] !== null
      ? (manifest['contributions'] as Record<string, unknown>)
      : {};
  return {
    ...manifest,
    contributions: {
      ...(skills === undefined ? {} : { skills }),
      ...(mcpServers === undefined ? {} : { mcpServers }),
      ...contributions,
    },
  };
}

/**
 * The services a Codex plugin's `.app.json` names, as catalog service ids: its keys, with
 * `google-drive` read as `google_drive`. ChatGPT app ids (`app-…`, `connector_…`) name no app.
 */
export function codexAppIds(appJson: unknown): string[] {
  const apps =
    typeof appJson === 'object' && appJson !== null && 'apps' in appJson ? appJson.apps : undefined;
  if (typeof apps !== 'object' || apps === null || Array.isArray(apps)) return [];
  return [
    ...new Set(
      Object.keys(apps)
        .map((key) =>
          key
            .toLowerCase()
            .replace(/-/g, '_')
            .replace(/_connector$/, ''),
        )
        .filter(
          (id) =>
            /^[a-z0-9_][a-z0-9_-]*$/.test(id) &&
            id.length <= 128 &&
            !/^(?:app|connector)_[0-9a-f]{16,}$/.test(id),
        ),
    ),
  ];
}

/**
 * A translated Codex manifest that brings these services: their connectors and accounts, and
 * the service it is about when it names one.
 */
export function withCodexAppConnectors(manifest: unknown, services: readonly string[]): unknown {
  if (services.length === 0 || typeof manifest !== 'object' || manifest === null) return manifest;
  const value = manifest as Record<string, unknown>;
  const contributions = (value['contributions'] ?? {}) as Record<string, unknown>;
  const requires = (value['requires'] ?? {}) as Record<string, unknown>;
  const connections = Array.isArray(requires['connections'])
    ? (requires['connections'] as Array<{ service?: unknown }>)
    : [];
  return {
    ...value,
    ...(value['service'] === undefined && services.length === 1 ? { service: services[0] } : {}),
    contributions: {
      ...contributions,
      connectors: contributions['connectors'] ?? services.map((service) => ({ service })),
    },
    requires: {
      ...requires,
      connections: [
        ...connections,
        ...services
          .filter((service) => !connections.some((connection) => connection.service === service))
          .map((service) => ({ service })),
      ],
    },
  };
}

export const marketplacePluginSchema = z
  .object({
    name: pluginNameSchema,
    source: z.discriminatedUnion('source', [
      z.object({ source: z.literal('local'), path: z.string().min(1) }).passthrough(),
      z.object({ source: z.literal('url'), url: z.url() }).passthrough(),
    ]),
    category: z.string().optional(),
    interface: z
      .object({ displayName: z.string().min(1).optional() })
      .passthrough()
      .optional(),
    policy: z
      .object({ installation: z.string().optional(), authentication: z.string().optional() })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type MarketplacePlugin = z.infer<typeof marketplacePluginSchema>;

export const marketplaceSchema = z
  .object({
    name: z.string(),
    interface: z.object({ displayName: z.string().optional() }).passthrough().optional(),
    plugins: z.array(marketplacePluginSchema),
  })
  .passthrough();

/** What a plugin brings, shown and filtered on in the catalog. */
export const PLUGIN_CONTRIBUTION_KINDS = [
  'skills',
  'mcp',
  'apps',
  'agents',
  'connectors',
  'resources',
  'themes',
] as const;
export const pluginContributionKindSchema = z.enum(PLUGIN_CONTRIBUTION_KINDS);
export type PluginContributionKind = z.infer<typeof pluginContributionKindSchema>;

/** The marketplace sources a catalog entry can come from (`adr-unified-plugins.md`). */
export const PLUGIN_CATALOG_SOURCES = [
  'codex',
  'claude-code',
  'provisioned',
  /** A `crowdex.json` package installed on the Crowdex account. */
  'account',
  'local',
  'user',
  'connector-services',
  'mcp-registry',
  'acp-registry',
] as const;
export const pluginCatalogSourceSchema = z.enum(PLUGIN_CATALOG_SOURCES);
export type PluginCatalogSource = z.infer<typeof pluginCatalogSourceSchema>;

/** Plugins the user keeps as files: local folders and user configuration. */
export function isUserPluginSource(source: PluginCatalogSource): boolean {
  return source === 'local' || source === 'user';
}

/** Plugins installed from a marketplace or generated from a registry. */
export function isMarketplacePluginSource(source: PluginCatalogSource): boolean {
  return (
    source === 'codex' ||
    source === 'claude-code' ||
    source === 'connector-services' ||
    source === 'mcp-registry' ||
    source === 'acp-registry'
  );
}

export const pluginCatalogItemSchema = z.object({
  name: pluginNameSchema,
  displayName: z.string(),
  description: z.string(),
  category: z.string().nullable(),
  logoUrl: z.string().nullable(),
  license: z.string().nullable(),
  version: z.string().nullable(),
  source: pluginCatalogSourceSchema,
  /** The service this plugin is about; entries of one service collapse into one. */
  service: z.string().nullable(),
  contributes: z.array(pluginContributionKindSchema),
  /** Other sources that offer the same plugin, collapsed into this entry. */
  otherSources: z.array(pluginCatalogSourceSchema).optional(),
  /** Where a marketplace plugin is installed from: a path of a GitHub repository. */
  marketplace: z
    .object({
      repository: z.url(),
      path: z.string().min(1).max(512),
      format: z.enum(['codex', 'claude-code']),
    })
    .optional(),
  authentication: z.string().nullable(),
  sourceUrl: z.string(),
  /** How the service's accounts are connected, and whether each way can be used here. */
  methods: z.array(connectionMethodSummarySchema).optional(),
  /** `coming-soon`: no method can be used yet and nothing works without an account. */
  availability: z.enum(['available', 'coming-soon']).optional(),
  mcpSource: z
    .object({
      label: z.string(),
      url: z.url(),
      kind: z.enum(['official-registry', 'manual']).optional(),
      registryName: z.string().optional(),
      registryVersion: z.string().optional(),
      resolvedAt: z.number().optional(),
    })
    .nullable()
    .optional(),
});
export type PluginCatalogItem = z.infer<typeof pluginCatalogItemSchema>;

export function pluginContributes(
  plugin: Pick<PluginCatalogItem, 'contributes'>,
  kind: PluginContributionKind,
): boolean {
  return plugin.contributes.includes(kind);
}

/** `contributes` with `kind` added or removed, in the canonical order. */
export function withPluginContribution(
  contributes: readonly PluginContributionKind[],
  kind: PluginContributionKind,
  present = true,
): PluginContributionKind[] {
  return PLUGIN_CONTRIBUTION_KINDS.filter((candidate) =>
    candidate === kind ? present : contributes.includes(candidate),
  );
}

/**
 * The connectors a manifest brings. A plugin about a service (`service`) brings that service's
 * connector unless it lists its connectors explicitly.
 */
export function pluginManifestConnectors(
  manifest: PluginManifest | null | undefined,
): PluginConnectorContribution[] {
  const declared = manifest?.contributions?.connectors;
  if (declared !== undefined) return declared;
  return manifest?.service === undefined
    ? []
    : [{ service: manifest.service, actions: 'inherit', triggers: 'inherit' }];
}

/** The contribution kinds a manifest declares, without reading the files it points to. */
export function pluginManifestContributes(
  manifest: PluginManifest | null | undefined,
): PluginContributionKind[] {
  if (!manifest) return [];
  const declared = (value: string | string[] | undefined) =>
    value !== undefined && (typeof value === 'string' || value.length > 0);
  const kinds = new Set<PluginContributionKind>();
  if (declared(manifest.contributions?.skills)) kinds.add('skills');
  if (declared(manifest.contributions?.mcpServers)) kinds.add('mcp');
  if (declared(manifest.apps)) kinds.add('apps');
  if ((manifest.contributions?.registryAgents?.length ?? 0) > 0) kinds.add('agents');
  if (pluginManifestConnectors(manifest).length > 0) kinds.add('connectors');
  if ((manifest.contributions?.resources?.length ?? 0) > 0) kinds.add('resources');
  return PLUGIN_CONTRIBUTION_KINDS.filter((kind) => kinds.has(kind));
}

const pluginMcpUrlSchema = z.union([
  z.url(),
  z.string().regex(/^(?:https?:\/\/|\$\{[A-Za-z_][A-Za-z0-9_]*\})\S+$/),
]);

/**
 * An MCP server a plugin brings. `${VAR}` in its URL, headers and environment are fields of its
 * `connection`, the type the plugin host generates for it (`mcpServerConnector`): the server
 * runs with the user's default connection of that type, leased from the connection hub, which
 * also signs in to a server that uses OAuth.
 */
export const pluginMcpServerSchema = z.union([
  z.object({
    type: z.literal('http'),
    name: z.string(),
    url: pluginMcpUrlSchema,
    headers: z.array(z.object({ name: z.string(), value: z.string() })),
    connection: serviceIdSchema.optional(),
  }),
  z.object({
    name: z.string(),
    command: z.string(),
    args: z.array(z.string()),
    env: z.array(z.object({ name: z.string(), value: z.string() })),
    connection: serviceIdSchema.optional(),
  }),
]);
export type PluginMcpServer = z.infer<typeof pluginMcpServerSchema>;

/** Main-to-renderer request emitted while an MCP server asks the user for structured input. */
export const mcpElicitationEventSchema = z.object({
  requestId: z.uuid(),
  message: z.string(),
  requestedSchema: z
    .object({
      type: z.literal('object'),
      properties: z.record(z.string(), z.unknown()).optional(),
      required: z.array(z.string()).optional(),
    })
    .passthrough(),
});
export type McpElicitationEvent = z.infer<typeof mcpElicitationEventSchema>;

export const mcpElicitationResultSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('accept'), content: z.record(z.string(), z.unknown()).optional() }),
  z.object({ action: z.literal('decline') }),
  z.object({ action: z.literal('cancel') }),
]);
export type McpElicitationResult = z.infer<typeof mcpElicitationResultSchema>;

export const pluginSkillSchema = z.object({
  name: pluginNameSchema,
  description: z.string().min(1),
  path: z.string().min(1),
  displayName: z.string().min(1).optional(),
  shortDescription: z.string().min(1).optional(),
  artifactKind: z.enum(['video', 'image', 'audio', 'document']).optional(),
});
export type PluginSkill = z.infer<typeof pluginSkillSchema>;

export function pluginSkillResourceUri(
  pluginName: string,
  skillName: string,
  path = 'SKILL.md',
): string {
  const encodedPath = path === '{+path}' ? path : path.split('/').map(encodeURIComponent).join('/');
  return `skill://${pluginName}/${encodeURIComponent(skillName)}/${encodedPath}`;
}

/** A plugin's file resource root, as installed: `path` is absolute, inside the package. */
export const pluginResourceRootSchema = pluginResourceContributionSchema.extend({
  path: z.string().min(1),
});
export type PluginResourceRoot = z.infer<typeof pluginResourceRootSchema>;

export function pluginResourceUri(pluginName: string, resourceId: string, path: string): string {
  const encodedPath = path === '{+path}' ? path : path.split('/').map(encodeURIComponent).join('/');
  return `resource://${pluginName}/${encodeURIComponent(resourceId)}/${encodedPath}`;
}

export const pluginSourceFileSchema = z.object({
  kind: z.enum(['manifest', 'skill', 'mcp']),
  label: z.string().min(1),
  path: z.string().min(1),
});
export type PluginSourceFile = z.infer<typeof pluginSourceFileSchema>;

export type PluginSourceWriteResult = { saved: true };

export { parsePluginSkill } from './plugin-skill';

/**
 * A contribution's key, for turning it off: `skill:<name>`, `mcp:<server>`, `resource:<id>`,
 * `agent:<id>`, `theme:<id>`, `connector:<app>` (the whole connector) or `connector:<app>/<action>`
 * (one action or trigger).
 */
export const pluginContributionKeySchema = z
  .string()
  .max(512)
  .regex(/^(?:skill|mcp|resource|agent|theme|connector):\S+$/);

export const pluginContributionKey = {
  skill: (name: string) => `skill:${name}`,
  mcp: (server: string) => `mcp:${server}`,
  resource: (id: string) => `resource:${id}`,
  agent: (id: string) => `agent:${id}`,
  theme: (id: string) => `theme:${id}`,
  connector: (service: string) => `connector:${service}`,
  action: (service: string, action: string) => `connector:${service}/${action}`,
} as const;

/** The actions and triggers of one connector the user turned off. */
export function disabledConnectorActions(
  plugin: Pick<InstalledPlugin, 'disabledContributions'>,
  service: string,
): string[] {
  const prefix = `${pluginContributionKey.connector(service)}/`;
  return (plugin.disabledContributions ?? [])
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length));
}

/**
 * The plugin as its consumers see it: contributions the user turned off move from the active lists
 * to `inactiveContributions`. Applied when an inventory is read, never to stored state.
 */
export function activePluginContributions(plugin: InstalledPlugin): InstalledPlugin {
  const disabled = new Set(plugin.disabledContributions ?? []);
  if (disabled.size === 0) {
    const { inactiveContributions: _, ...active } = plugin;
    return active;
  }
  const split = <T>(items: readonly T[], key: (item: T) => string) => ({
    on: items.filter((item) => !disabled.has(key(item))),
    off: items.filter((item) => disabled.has(key(item))),
  });
  const skills = split(plugin.skills, (skill) => pluginContributionKey.skill(skill.name));
  const mcpServers = split(plugin.mcpServers, (server) => pluginContributionKey.mcp(server.name));
  const resources = split(plugin.resources ?? [], (resource) =>
    pluginContributionKey.resource(resource.id),
  );
  const connectors = split(plugin.connectors ?? [], (connector) =>
    pluginContributionKey.connector(connector.service),
  );
  const registryAgents = split(plugin.registryAgents ?? [], pluginContributionKey.agent);
  return {
    ...plugin,
    skills: skills.on,
    skillPaths: plugin.skillPaths.filter((path) =>
      skills.on.some((skill) => skill.path.startsWith(path)),
    ),
    mcpServers: mcpServers.on,
    ...(plugin.resources === undefined ? {} : { resources: resources.on }),
    ...(plugin.connectors === undefined ? {} : { connectors: connectors.on }),
    ...(plugin.registryAgents === undefined ? {} : { registryAgents: registryAgents.on }),
    inactiveContributions: {
      skills: skills.off,
      mcpServers: mcpServers.off,
      resources: resources.off,
      connectors: connectors.off,
      ...(registryAgents.off.length === 0 ? {} : { registryAgents: registryAgents.off }),
    },
  };
}

/**
 * The ACP agents an inventory offers: those its enabled plugins bring from the public registry.
 * Only these (and those of enabled apps) are offered in agent settings.
 */
export function offeredAcpAgentIds(plugins: readonly InstalledPlugin[]): Set<string> {
  return new Set(
    plugins
      .filter((plugin) => plugin.enabled && !plugin.removed)
      .flatMap((plugin) => plugin.registryAgents ?? []),
  );
}

/**
 * Skills installed on this computer outside Crowdex (by the `skills` CLI, or by hand) in a root
 * other agents read too (`~/.agents/skills`, `~/.crowdex/skills`), grouped by where they came
 * from: the source a lock file names, else the root itself.
 */
export const machineSkillGroupSchema = z.object({
  /** The root holding the skills' folders (absolute). */
  root: z.string().min(1),
  /** The root as shown: `~/.agents/skills`. */
  label: z.string().min(1),
  /** Whether other agents of this computer (Codex…) read this root too, as `~/.agents/skills`. */
  shared: z.boolean(),
  /** The source the lock file names (`owner/repo`); absent for folders found without one. */
  source: z.string().min(1).optional(),
  sourceUrl: z.string().min(1).optional(),
  /** Each skill's folder (absolute): what "Remove from this computer" deletes. */
  folders: z.array(z.string().min(1)),
  /**
   * Its skills another enabled plugin provides too, with that plugin's title: agents get the
   * plugin's copy, never this one.
   */
  alsoIn: z.array(z.object({ skill: z.string().min(1), plugin: z.string().min(1) })).optional(),
});
export type MachineSkillGroup = z.infer<typeof machineSkillGroupSchema>;

export const installedPluginSchema = pluginCatalogItemSchema.extend({
  settings: pluginSettingsSchema.optional(),
  removed: z.boolean().optional(),
  enabled: z.boolean(),
  installedAt: z.number(),
  contentDigest: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  installPath: z.string(),
  skillPaths: z.array(z.string()),
  skills: z.array(pluginSkillSchema),
  skillsIndexedAt: z.number().optional(),
  skillsIndexVersion: z.number().int().positive().optional(),
  mcpServers: z.array(pluginMcpServerSchema),
  connectors: z.array(pluginConnectorContributionSchema).optional(),
  resources: z.array(pluginResourceRootSchema).optional(),
  /** The ACP registry agents the plugin offers. */
  registryAgents: z.array(acpRegistryAgentIdSchema).optional(),
  /** What an import from another plugin format left out, shown with the plugin. */
  importNotices: z.array(z.string().max(512)).max(16).optional(),
  /** Contributions the user turned off (`pluginContributionKey`); the plugin stays enabled. */
  disabledContributions: z.array(pluginContributionKeySchema).max(4096).optional(),
  /**
   * What the user turned off, set by inventory reads (`activePluginContributions`): the active
   * lists omit it, so every consumer sees only what is on, and the UI can still turn it back on.
   */
  inactiveContributions: z
    .object({
      skills: z.array(pluginSkillSchema),
      mcpServers: z.array(pluginMcpServerSchema),
      resources: z.array(pluginResourceRootSchema),
      connectors: z.array(pluginConnectorContributionSchema),
      registryAgents: z.array(acpRegistryAgentIdSchema).optional(),
    })
    .optional(),
  mcpRegistryCheckedAt: z.number().optional(),
  localSourcePath: z.string().optional(),
  discoveryScope: z.enum(['user', 'workspace']).optional(),
  discoveryProvider: z.string().min(1).optional(),
  sourceFiles: z.array(pluginSourceFileSchema).optional(),
  /** Present when the entry is a group of skills installed on this computer. */
  machineSkills: machineSkillGroupSchema.optional(),
  requires: pluginRequiresSchema.optional(),
  /** Present when the plugin is built from a `crowdex.json` (see `crowdexPluginDetailsSchema`). */
  crowdex: crowdexPluginDetailsSchema.optional(),
});
export type InstalledPlugin = z.infer<typeof installedPluginSchema>;

/** The titles of the enabled plugins other than this computer's skill groups, by skill name. */
function skillProviders(plugins: readonly InstalledPlugin[]): Map<string, string> {
  const providers = new Map<string, string>();
  for (const plugin of plugins) {
    if (plugin.machineSkills !== undefined || !plugin.enabled || plugin.removed) continue;
    const names = [
      ...plugin.skills.map((skill) => skill.name),
      ...(plugin.crowdex?.skills ?? []).map((skill) => skill.name),
    ];
    for (const name of names) if (!providers.has(name)) providers.set(name, plugin.displayName);
  }
  return providers;
}

/**
 * The inventory with each group of skills of this computer told which of its skills an enabled
 * plugin provides too (`machineSkills.alsoIn`): that plugin's copy is the one agents get.
 */
export function withMachineSkillDuplicates(plugins: readonly InstalledPlugin[]): InstalledPlugin[] {
  if (!plugins.some((plugin) => plugin.machineSkills !== undefined)) return [...plugins];
  const providers = skillProviders(plugins);
  return plugins.map((plugin) => {
    const group = plugin.machineSkills;
    if (group === undefined) return plugin;
    const alsoIn = [...plugin.skills, ...(plugin.inactiveContributions?.skills ?? [])].flatMap(
      (skill) => {
        const provider = providers.get(skill.name);
        return provider === undefined ? [] : [{ skill: skill.name, plugin: provider }];
      },
    );
    const { alsoIn: _, ...rest } = group;
    return { ...plugin, machineSkills: alsoIn.length === 0 ? rest : { ...rest, alsoIn } };
  });
}

const discoveryParamsSchema = z.object({ workspaceRoot: z.string().min(1).optional() }).strict();
const deviceMcpParamsSchema = z
  .object({
    workspaceRoot: z.string().min(1).optional(),
    pluginId: pluginNameSchema,
  })
  .strict();
const pluginParamsSchema = z.object({ plugin: pluginCatalogItemSchema }).strict();
const settingsParamsSchema = z
  .object({
    name: pluginNameSchema,
    workspaceRoot: z.string().min(1).max(4096).optional(),
    project: z.string().min(1).max(4096).optional(),
  })
  .strict();
export const pluginRpcRequestSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('settings.projects'), params: discoveryParamsSchema }),
  z.object({ method: z.literal('settings.get'), params: settingsParamsSchema }),
  z.object({
    method: z.literal('settings.update'),
    params: settingsParamsSchema.extend({
      patch: z
        .record(z.string().max(96), pluginSettingValueSchema.nullable())
        .refine((value) => Object.keys(value).length <= 100),
    }),
  }),
  z.object({ method: z.literal('installed.list'), params: discoveryParamsSchema }),
  z.object({ method: z.literal('install'), params: pluginParamsSchema }),
  z.object({
    method: z.literal('local.install'),
    params: z
      .object({
        path: z.string().min(1),
        /**
         * For a `crowdex.json` folder, what the user approves: without it the answer is what
         * installing would grant (`LocalPluginPreview`).
         */
        grants: z.array(z.string().max(512)).max(256).optional(),
      })
      .strict(),
  }),
  z.object({
    method: z.literal('update.approve'),
    params: z.object({ name: pluginNameSchema }).strict(),
  }),
  z.object({
    method: z.literal('publish'),
    params: z.object({ name: pluginNameSchema }).strict(),
  }),
  z.object({
    method: z.literal('source.read'),
    params: z
      .object({
        name: pluginNameSchema,
        path: z.string().min(1),
        workspaceRoot: z.string().min(1).optional(),
      })
      .strict(),
  }),
  z.object({
    method: z.literal('source.write'),
    params: z
      .object({
        name: pluginNameSchema,
        path: z.string().min(1),
        content: z.string().max(1_000_000),
        workspaceRoot: z.string().min(1).optional(),
      })
      .strict(),
  }),
  z.object({
    /**
     * Deletes skill folders of this computer (a `machineSkills` group's `folders`) and their
     * lock entries: only folders directly inside a known skills root.
     */
    method: z.literal('skills.machine.remove'),
    params: z.object({ folders: z.array(z.string().min(1).max(4096)).min(1).max(512) }).strict(),
  }),
  z.object({
    method: z.literal('uninstall'),
    params: z.object({ name: pluginNameSchema, workspaceRoot: z.string().optional() }).strict(),
  }),
  z.object({
    method: z.literal('connectors.service'),
    params: z.object({ service: serviceIdSchema, workspaceRoot: z.string().optional() }).strict(),
  }),
  z.object({
    method: z.literal('connectors.action'),
    params: z
      .object({
        service: serviceIdSchema,
        action: z.string().min(1).max(320),
        workspaceRoot: z.string().optional(),
      })
      .strict(),
  }),
  z.object({
    method: z.literal('connectors.tools'),
    params: z
      .object({
        service: serviceIdSchema,
        method: z.string().min(1).max(160),
        workspaceRoot: z.string().optional(),
      })
      .strict(),
  }),
  z.object({
    /** Lets a command run without the OS sandbox on this machine (Windows has none). */
    method: z.literal('commands.unsandboxed.set'),
    params: z
      .object({
        name: pluginNameSchema,
        command: z.string().min(1).max(64),
        allowed: z.boolean(),
        workspaceRoot: z.string().optional(),
      })
      .strict(),
  }),
  z.object({
    method: z.literal('contributions.enabled.set'),
    params: z
      .object({
        name: pluginNameSchema,
        key: pluginContributionKeySchema,
        enabled: z.boolean(),
        workspaceRoot: z.string().optional(),
      })
      .strict(),
  }),
  z.object({
    method: z.literal('enabled.set'),
    params: z
      .object({
        name: pluginNameSchema,
        enabled: z.boolean(),
        workspaceRoot: z.string().optional(),
      })
      .strict(),
  }),
  z.object({ method: z.literal('mcp.portable.list'), params: discoveryParamsSchema }),
  z.object({ method: z.literal('mcp.apps.list'), params: discoveryParamsSchema }),
  z.object({ method: z.literal('mcp.device-tools.list'), params: deviceMcpParamsSchema }),
  z.object({ method: z.literal('mcp.status'), params: discoveryParamsSchema }),
  z.object({
    method: z.literal('mcp.reconnect'),
    params: z
      .object({ serverId: z.string().min(1), workspaceRoot: z.string().min(1).optional() })
      .strict(),
  }),
  z.object({
    method: z.literal('mcp.device-tools.call'),
    params: z
      .object({
        workspaceRoot: z.string().min(1).optional(),
        pluginId: pluginNameSchema,
        name: z.string().min(1),
        arguments: z.record(z.string(), z.unknown()),
      })
      .strict(),
  }),
  z.object({
    method: z.literal('mcp.resource.read'),
    params: z.object({ uri: z.string().min(1) }).strict(),
  }),
  z.object({
    method: z.literal('mcp.tools.call'),
    params: z
      .object({
        uri: z.string().min(1),
        name: z.string().min(1),
        arguments: z.record(z.string(), z.unknown()),
      })
      .strict(),
  }),
]);
export type PluginRpcRequest = z.infer<typeof pluginRpcRequestSchema>;
export * from './widget-transform';
export * from './widgets';
